import { create } from 'zustand';
import { getUserPref, setUserPref } from '../hooks/useUserPreferences';

// ============================================================================
// Tour Store
//
// Tracks which guided tours have been completed per-user, and whether
// the user has globally opted out of tours.
// ============================================================================

export const TOUR_IDS = {
  DASHBOARD: 'dashboard',
  RESIDENTS: 'residents',
  SELECTIVE_TOUCH: 'selectiveTouch',
  REPORTS: 'reports',
} as const;

export type TourId = (typeof TOUR_IDS)[keyof typeof TOUR_IDS];

const PREF_KEY_COMPLETED = 'tours.completed';
const PREF_KEY_DISABLED = 'tours.disabled';

interface TourState {
  /** Currently active tour (null = none running) */
  activeTourId: TourId | null;
  /** Set of completed tour IDs (loaded from user prefs) */
  completedTours: Set<TourId>;
  /** Global opt-out */
  toursDisabled: boolean;
  /** Whether the tour-list dialog is open */
  tourListOpen: boolean;

  // Actions
  startTour: (id: TourId) => void;
  completeTour: (id: TourId, userId: number) => void;
  cancelTour: () => void;
  resetTour: (id: TourId, userId: number) => void;
  resetAllTours: (userId: number) => void;
  setToursDisabled: (disabled: boolean, userId: number) => void;
  loadFromPrefs: (userId: number) => void;
  openTourList: () => void;
  closeTourList: () => void;
}

export const useTourStore = create<TourState>((set) => ({
  activeTourId: null,
  completedTours: new Set(),
  toursDisabled: false,
  tourListOpen: false,

  startTour: (id) =>
    set({ activeTourId: id, tourListOpen: false }),

  completeTour: (id, userId) =>
    set((state) => {
      const next = new Set(state.completedTours);
      next.add(id);
      setUserPref(userId, PREF_KEY_COMPLETED, Array.from(next));
      return { completedTours: next, activeTourId: null };
    }),

  cancelTour: () =>
    set({ activeTourId: null }),

  resetTour: (id, userId) =>
    set((state) => {
      const next = new Set(state.completedTours);
      next.delete(id);
      setUserPref(userId, PREF_KEY_COMPLETED, Array.from(next));
      return { completedTours: next };
    }),

  resetAllTours: (userId) => {
    setUserPref(userId, PREF_KEY_COMPLETED, []);
    set({ completedTours: new Set() });
  },

  setToursDisabled: (disabled, userId) => {
    setUserPref(userId, PREF_KEY_DISABLED, disabled);
    set({ toursDisabled: disabled });
  },

  loadFromPrefs: (userId) => {
    const completed = getUserPref<string[]>(userId, PREF_KEY_COMPLETED, []);
    const disabled = getUserPref<boolean>(userId, PREF_KEY_DISABLED, false);
    set({
      completedTours: new Set(completed as TourId[]),
      toursDisabled: disabled,
    });
  },

  openTourList: () => set({ tourListOpen: true }),
  closeTourList: () => set({ tourListOpen: false }),
}));
